import { useVirtualizer } from "@tanstack/react-virtual";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import styles from "./YearSelector.module.scss";
import cx from "classnames";
import { useYearSelectorKeys } from "./internal/useYearSelectorKeys";
import { useOptionallyControlledState, usePrevious } from "../../hooks";
import { useMergeRefs } from "../../../hooks";
import { YearItem } from "./internal/YearItem";
import { PassThroughProps } from "../../../types";

const DEFAULT_MIN = 1900;
const DEFAULT_MAX = 2200;

export interface YearSelectorProps {
  /**
   * The minimum selectable year
   * Defaults to 1900.
   */
  min?: number;
  /**
   * The maximum selectable year.
   * Defaults to 2200.
   */
  max?: number;
  /**
   * The currently selected year
   */
  value?: number | null;
  /**
   * The default value of the selected year
   */
  defaultValue?: number;
  /**
   * The function to call when the selected year changes
   *
   * @param year - The year that is selected.
   */
  onChange?: (year: number | null) => void;
  /**
   * The function to call when the selected year is affirmatively changed.
   * This is called when the user confirms the selection by pressing Enter, pressing Space, or clicking.
   *
   * @param year - The year that is selected.
   */
  onConfirm?: (year: number | null) => void;
  /**
   * If a `value` or `defaultValue` is not provided, this will be used as the starting scroll position (and initial focus for accessibility).
   */
  startingYear?: number;
  /**
   * The number of columns to display.
   * @default 2
   */
  columns?: number;
  /**
   * The class name to apply to the component.
   */
  className?: string;
  /**
   * Controls whether a year can be deselected, i.e. set to null.
   * @default false
   */
  required?: boolean;
  /**
   * Controls whether the component allows the user to select a year.
   * @default false
   */
  disabled?: boolean;
  /**
   * The aria-label for the component.
   * @default "Year selection"
   */
  ariaLabel?: string;
  /**
   * Props forwarded onto the root element of the component.
   */
  rootProps?: PassThroughProps<"div">;
}
const currentYear = new Date().getFullYear();

export const YearSelector = forwardRef<HTMLDivElement, YearSelectorProps>(
  (
    {
      min = DEFAULT_MIN,
      max = DEFAULT_MAX,
      value: valueProp,
      defaultValue: defaultValueProp,
      onChange,
      onConfirm,
      startingYear = currentYear,
      columns: columnsProp,
      className,
      required = false,
      disabled = false,
      ariaLabel = "Year selection",
      rootProps,
    },
    ref,
  ) => {
    const columns = Math.max(columnsProp ?? 2, 1);
    const [value, setValue] = useOptionallyControlledState<number | null>({
      controlledValue: valueProp,
      defaultValue: defaultValueProp ?? null,
      onChange,
    });
    const previousValue = usePrevious(value);
    const initialFocusYear = value ?? startingYear;
    const [pseudoFocused, setPseudoFocused] =
      useState<number>(initialFocusYear);

    const handleChange = (year: number | null) => {
      if (!required && value === year) {
        setValue(null);
        return;
      }
      setValue(year);
      if (year !== null) {
        setPseudoFocused(year);
      }
    };

    const handleItemChange = (year: number) => {
      handleChange(year);
      onConfirm?.(year);
    };

    const gap = 12;
    const rowHeight = 58;
    const rowCount = Math.ceil((max - min + 1) / columns);
    const initialOffset =
      ((initialFocusYear - min) * (rowHeight + gap)) / columns + gap / 2;

    // The virtualizer
    const rowVirtualizer = useVirtualizer({
      count: rowCount,
      getScrollElement: () => parentRef.current,
      estimateSize: () => rowHeight,
      gap,
      initialOffset,
      paddingEnd: gap / 2,
    });

    const scrollToYear = useCallback(
      (year: number, { focus }: { focus?: boolean } = {}) => {
        const index = Math.floor((year - min) / columns);
        rowVirtualizer.scrollToIndex(index, { align: "center" });
        if (focus) {
          setPseudoFocused(year);
        }
      },
      [min, columns, rowVirtualizer, setPseudoFocused],
    );

    const parentRef = useRef(null);
    const { listRef } = useYearSelectorKeys({
      columns,
      scrollToYear,
      initialFocus: initialFocusYear,
      onSelect: handleChange,
      onConfirm: () => onConfirm?.(value),
      min,
      max,
      disabled,
      pseudoFocused,
      setPseudoFocused,
      required,
    });

    useEffect(() => {
      if (value !== null && previousValue !== value) {
        scrollToYear(value, { focus: true });
      }
    }, [value, previousValue, scrollToYear, listRef]);

    const jointRef = useMergeRefs([parentRef, listRef, ref]);

    return (
      <>
        {/* The scrollable element */}
        <div
          ref={jointRef}
          aria-label={ariaLabel}
          tabIndex={0}
          className={cx(styles["year-selector"], className)}
          role="spinbutton"
          aria-valuenow={value ?? undefined}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuetext={value ? `${value}` : "No year selected"}
          data-test={rowVirtualizer.options.paddingEnd}
          style={{
            maxHeight: `${
              rowVirtualizer.getTotalSize() + 8 // Accounts for the padding of the container
            }px`,
          }}
          {...rootProps}
        >
          {/* The large inner element to hold all of the items */}
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {/* Only the visible items in the virtualizer, manually positioned to be in view */}
            {rowVirtualizer.getVirtualItems().map((virtualItem) => {
              const years = Array.from(
                { length: columns },
                (_, i) => virtualItem.index * columns + i + min,
              );
              return (
                <div
                  key={virtualItem.key}
                  data-testid="v-row"
                  className={styles["year-grid"]}
                  style={{
                    transform: `translateY(${virtualItem.start}px)`,
                    gridTemplateColumns: `repeat(${columns}, 1fr)`,
                  }}
                >
                  {years.map((year) => {
                    if (year > max) return <div key={year} />;
                    return (
                      <YearItem
                        key={year}
                        disabled={disabled}
                        year={year}
                        onChange={handleItemChange}
                        selected={value === year}
                        pseudoFocused={pseudoFocused === year}
                        onFocus={() => {
                          listRef.current?.focus();
                          setPseudoFocused(year);
                        }}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  },
);

YearSelector.displayName = "YearSelector";
